// GENERATED by scripts/sync-blockgate-deno.mjs from src/ — DO NOT EDIT.
// Edit the src/ originals, then run `npm run sync:blockgate`.

// TSL Block text format — human-readable surface for a whole block, compiling ONLY to the safe AST.
// Round-trips with TslBlock (parseBlock(printBlock(b)) reproduces b). Used by the TSL Block Studio text
// editor; the visual builder edits the same TslBlock. No JavaScript is accepted at any point.
//
//   indicator {
//     param period: int 5..100 = 20
//     state acc = 0 -> (state.acc + close)
//     value: (close / ema(close, period)) - 1
//     signal: (value > 0)            // 'value' is NOT a binding — sections are independent expressions
//   }

import { RISK_CTX, SIZING_CTX, SOURCES, TSL_DSL_VERSION, type Expr, type TslBlock } from './ast.ts';
import { parseExpr, printExpr, type ParseCtx } from './parse.ts';
import { validateTslParamSpec } from './tslValidate.ts';
import type { BlockCategory, CustomBlockSubtype, ParamSpecBound, ParameterSchema } from './customBlockLibrary.ts';

export const categoryForBlockType = (bt: CustomBlockSubtype): BlockCategory =>
  bt === 'filter' ? 'filters'
    : (bt === 'qty_calculator' || bt === 'sl_calculator' || bt === 'tp_calculator') ? 'sizing'
      : bt === 'risk_rule' ? 'risk_rules' : 'indicators';

const ctxFieldsFor = (cat: BlockCategory): Set<string> => new Set(cat === 'sizing' ? SIZING_CTX : cat === 'risk_rules' ? RISK_CTX : []);

// ── printer ─────────────────────────────────────────────────────────────────────
function printParam(s: ParamSpecBound): string {
  const compact = s.kind === 'bool' ? `bool = ${s.default}`
    : s.kind === 'enum' ? `enum ${JSON.stringify(s.values ?? [])} = ${JSON.stringify(s.default)}`
      : `${s.kind} ${s.min}..${s.max} = ${s.default}${s.optimizable === false ? ' fixed' : ''}`;
  const parsed = parseParam(compact);
  const key = (o: ParamSpecBound) => JSON.stringify(Object.entries(o).filter(([, v]) => v !== undefined).sort(([a], [b]) => a.localeCompare(b)));
  // Preserve optimizer steps, labels, bool/enum mutability, numeric enums and
  // optional bounds. The shorthand must never silently change the parameter space.
  return 'spec' in parsed && key(parsed.spec) === key(s) ? compact : `spec ${JSON.stringify(s)}`;
}

export function printBlock(b: TslBlock): string {
  const L: string[] = [`${b.blockType} {`];
  for (const [name, s] of Object.entries(b.params ?? {})) L.push(`  param ${name}: ${printParam(s)}`);
  for (const [name, s] of Object.entries(b.state ?? {})) L.push(`  state ${name} = ${s.init} -> ${printExpr(s.update)}`);
  if (b.value) L.push(`  value: ${printExpr(b.value)}`);
  if (b.signal) L.push(`  signal: ${printExpr(b.signal)}`);
  if (b.qty) L.push(`  qty: ${printExpr(b.qty)}`);
  if (b.risk) L.push(`  risk: ${printExpr(b.risk.action)}`);
  L.push('}');
  return L.join('\n');
}

// ── parser ────────────────────────────────────────────────────────────────────
export type BlockParseResult = { ok: true; block: TslBlock } | { ok: false; errors: Array<{ line: number; error: string }> };

const BLOCK_TYPES = new Set<CustomBlockSubtype>(['indicator', 'pattern', 'analysis', 'filter', 'signal_generator', 'qty_calculator', 'sl_calculator', 'tp_calculator', 'risk_rule']);

// ── Deterministic parse budgets (entry-point caps; the semantic validator applies the tighter publish
// budgets of 16 params / 8 state afterwards — these exist so parsing itself is bounded and fail-closed). ──
/** Max characters for one TSL block's source text (64 KB — real blocks are well under 1 KB). */
export const MAX_BLOCK_TEXT = 65536;
/** Max characters for one declared param/state identifier. */
export const MAX_DECL_IDENT = 64;
/** Parse-time ceilings on declaration counts (structural, far above the publish budgets). */
export const MAX_DECL_PARAMS = 64;
export const MAX_DECL_STATE = 64;

const NUMBER_TEXT = '-?(?:[0-9]+(?:\\.[0-9]+)?|\\.[0-9]+)(?:[eE][+-]?[0-9]+)?';
function stripComment(line: string): string {
  let quote = '';
  for (let i = 0; i < line.length; i++) {
    const c = line[i];
    if (quote) { if (c === '\\') i++; else if (c === quote) quote = ''; }
    else if (c === '"' || c === "'") quote = c;
    else if (c === '/' && line[i + 1] === '/') return line.slice(0, i);
  }
  return line;
}
function parseParam(rest: string): { spec: ParamSpecBound } | { error: string } {
  if (rest.startsWith('spec ')) {
    let spec: unknown; try { spec = JSON.parse(rest.slice(5)); } catch { return { error: 'invalid parameter spec JSON' }; }
    const error = validateTslParamSpec(spec);
    return error ? { error } : { spec: spec as ParamSpecBound };
  }
  // "<kind> <min>..<max> = <default> [fixed]" | "bool = <default>" | "enum [a, b] = <default>"
  const m = rest.match(/^(int|float|bool|enum)\s*(.*)$/);
  if (!m) return { error: `bad param type in '${rest}'` };
  const kind = m[1] as ParamSpecBound['kind']; const tail = m[2].trim();
  if (kind === 'bool') { const m = tail.match(/^=\s*(true|false)$/); if (!m) return { error: 'boolean default must be true or false' }; return { spec: { kind, default: m[1] === 'true', optimizable: false } }; }
  if (kind === 'enum') {
    const em = tail.match(/^\[(.*)\]\s*=\s*(.*)$/); if (!em) return { error: `bad enum '${tail}'` };
    let values: string[];
    try { values = JSON.parse(`[${em[1]}]`); }
    catch {
      // Legacy unquoted enum identifiers remain valid; complex strings use JSON.
      values = em[1].split(',').map(x => x.trim());
      if (!values.every(x => /^[A-Za-z_][A-Za-z0-9_]*$/.test(x))) return { error: 'enum values must be JSON strings or bare identifiers' };
    }
    let def: unknown; try { def = JSON.parse(em[2].trim()); } catch { def = em[2].trim(); }
    if (!Array.isArray(values) || !values.length || !values.every(x => typeof x === 'string') || !values.includes(def as string)) return { error: 'enum default must belong to its string values' };
    return { spec: { kind, values, default: def, optimizable: false } };
  }
  const nm = tail.match(new RegExp(`^(${NUMBER_TEXT})\\.\\.(${NUMBER_TEXT})\\s*=\\s*(${NUMBER_TEXT})\\s*(fixed)?$`));
  if (!nm) return { error: `bad numeric param '${tail}' (expected 'min..max = default')` };
  const numbers = nm.slice(1, 4).map(Number);
  if (!numbers.every(Number.isFinite) || (kind === 'int' && !numbers.every(Number.isInteger)) || numbers[0] > numbers[1] || numbers[2] < numbers[0] || numbers[2] > numbers[1]) return { error: 'numeric bounds/default must be finite, ordered, in range, and integers for int' };
  return { spec: { kind, min: Number(nm[1]), max: Number(nm[2]), default: Number(nm[3]), optimizable: !nm[4] } };
}

export function parseBlock(text: string): BlockParseResult {
  if (typeof text !== 'string') return { ok: false, errors: [{ line: 1, error: 'block text must be a string' }] };
  if (text.length > MAX_BLOCK_TEXT) return { ok: false, errors: [{ line: 1, error: `block text too large (${text.length} > ${MAX_BLOCK_TEXT} chars)` }] };
  const rawLines = text.split('\n');
  const errors: Array<{ line: number; error: string }> = [];
  // strip comments + blank lines, keep original line numbers
  const lines = rawLines.map((l, i) => ({ i: i + 1, s: stripComment(l).trim() })).filter((l) => l.s.length > 0);
  if (lines.length < 2) return { ok: false, errors: [{ line: 1, error: 'empty block' }] };

  const head = lines[0].s.match(/^(\w+)\s*\{$/);
  if (!head || !BLOCK_TYPES.has(head[1] as CustomBlockSubtype)) return { ok: false, errors: [{ line: lines[0].i, error: `expected '<blockType> {' (got '${lines[0].s}')` }] };
  const blockType = head[1] as CustomBlockSubtype;
  const category = categoryForBlockType(blockType);
  if (lines[lines.length - 1].s !== '}') errors.push({ line: lines[lines.length - 1].i, error: "expected closing '}'" });

  const params: ParameterSchema = {}; const stateRaw: Array<{ name: string; init: number; updateSrc: string; line: number }> = [];
  const sections: Record<string, { src: string; line: number }> = {};
  // reserved JS object-machinery names can never be param/state identifiers — assigning
  // through them mutates prototype links instead of adding data properties (hardening;
  // shared blocks parse through here on every consumer's machine).
  const RESERVED_NAMES = new Set(['__proto__', 'constructor', 'prototype']);
  const reservedParams = new Set<string>([...RESERVED_NAMES, ...SOURCES, ...ctxFieldsFor(category), 'true', 'false', 'and', 'or', 'not']);
  for (const { i, s } of lines.slice(1, -1)) {
    let m: RegExpMatchArray | null;
    if ((m = s.match(/^param\s+(\w+)\s*:\s*(.+)$/))) {
      if (reservedParams.has(m[1]) || !/^[A-Za-z_][A-Za-z0-9_]*$/.test(m[1])) { errors.push({ line: i, error: `reserved or invalid name '${m[1]}' cannot be a param` }); continue; }
      if (Object.prototype.hasOwnProperty.call(params, m[1])) { errors.push({ line: i, error: `duplicate param '${m[1]}'` }); continue; }
      if (m[1].length > MAX_DECL_IDENT) { errors.push({ line: i, error: `param name too long (> ${MAX_DECL_IDENT} chars)` }); continue; }
      if (Object.keys(params).length >= MAX_DECL_PARAMS) { errors.push({ line: i, error: `too many params (> ${MAX_DECL_PARAMS})` }); continue; }
      const r = parseParam(m[2].trim()); if ('error' in r) errors.push({ line: i, error: r.error }); else params[m[1]] = r.spec;
    }
    else if ((m = s.match(new RegExp(`^state\\s+(\\w+)\\s*=\\s*(${NUMBER_TEXT})\\s*->\\s*(.+)$`)))) {
      if (RESERVED_NAMES.has(m[1])) { errors.push({ line: i, error: `reserved name '${m[1]}' cannot be a state` }); continue; }
      if (!/^[A-Za-z_][A-Za-z0-9_]*$/.test(m[1]) || !Number.isFinite(Number(m[2]))) { errors.push({ line: i, error: 'invalid state identifier or initial value' }); continue; }
      if (stateRaw.some(x => x.name === m![1])) { errors.push({ line: i, error: `duplicate state '${m[1]}'` }); continue; }
      if (m[1].length > MAX_DECL_IDENT) { errors.push({ line: i, error: `state name too long (> ${MAX_DECL_IDENT} chars)` }); continue; }
      if (stateRaw.length >= MAX_DECL_STATE) { errors.push({ line: i, error: `too many state vars (> ${MAX_DECL_STATE})` }); continue; }
      stateRaw.push({ name: m[1], init: Number(m[2]), updateSrc: m[3].trim(), line: i });
    }
    else if ((m = s.match(/^(value|signal|qty|risk)\s*:\s*(.+)$/))) {
      if (sections[m[1]]) errors.push({ line: i, error: `duplicate section '${m[1]}'` });
      else sections[m[1]] = { src: m[2].trim(), line: i };
    }
    else errors.push({ line: i, error: `unrecognized line '${s}'` });
  }

  const ctx: ParseCtx = { ctxFields: ctxFieldsFor(category), paramNames: new Set(Object.keys(params)), stateNames: new Set(stateRaw.map((s) => s.name)) };
  const parseSec = (src: string, line: number): Expr | undefined => { const r = parseExpr(src, ctx); if (!r.ok) { errors.push({ line, error: `${r.error} (at col ${r.pos + 1})` }); return undefined; } return r.expr; };

  const state: TslBlock['state'] = {};
  for (const s of stateRaw) { const u = parseSec(s.updateSrc, s.line); if (u) state![s.name] = { init: s.init, update: u }; }

  const block: TslBlock = { dsl: TSL_DSL_VERSION, category, blockType, params, state: Object.keys(state!).length ? state : undefined };
  if (sections.value) block.value = parseSec(sections.value.src, sections.value.line);
  if (sections.signal) block.signal = parseSec(sections.signal.src, sections.signal.line);
  if (sections.qty) block.qty = parseSec(sections.qty.src, sections.qty.line);
  if (sections.risk) { const a = parseSec(sections.risk.src, sections.risk.line); if (a) block.risk = { action: a }; }

  if (errors.length) return { ok: false, errors };
  return { ok: true, block };
}
